import { useEffect, useMemo } from "react";

import {
  compact,
  every,
  find,
  first,
  flatMap,
  groupBy,
  isEmpty,
  map,
  sortBy,
} from "lodash/fp";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { EMPTY_TRANSCRIPT_SUMMARY } from "@app/analytics/api/models";
import type { Segment } from "@app/analytics/components/PlayerControls/types";
import useSessionChaptersQuery from "@app/analytics/hooks/meetings/useSessionChaptersQuery";
import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscriptReactions from "@app/analytics/MeetingReport/hooks/useTranscriptReactions";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import type { TranscriptItem } from "@app/analytics/MeetingReport/types";
import { generateTranscriptItems } from "@app/analytics/MeetingReport/utils";
import {
  anyAreDeleted,
  anyAreEdited,
  getItemsAndEditedStatus,
} from "@app/analytics/MeetingReport/utils";
import {
  UPSELL_SUMMARY_CONTENT,
  UPSELL_TRANSCRIPT_TURNS,
} from "@app/analytics/MeetingReport/utils/upsellPreviewContent";
import { DEFAULT_LANGUAGE_CODE } from "@app/common/constants/locales";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type {
  BaseSessionChapter,
  BaseTranscriptTurn,
  SessionChapter,
  SessionHighlight,
  TranscriptClipType,
  TranscriptKeyword,
  TranscriptSpeaker,
  TranscriptSummary,
  TranscriptSummaryItem,
  TranscriptTurn,
  TranscriptTurnParagraph,
} from "@app/common/types";
import {
  getDelimiterForLanguage,
  getFormattedTimestampFromMs,
  getRelativeTimestamp,
  isWithinRange,
  msDurationBetween,
} from "@app/common/utils";

import useMeetingReport from "./useMeetingReport";
import useTranscriptQuery from "./useTranscriptQuery";

const DEFAULT_OPTS = { enabled: true };

const EMPTY_TURNS: TranscriptTurn[] = [];
const EMPTY_CHAPTERS: SessionChapter[] = [];
const EMPTY_SEGMENTS: Segment[] = [];
const EMPTY_SPEAKERS: TranscriptSpeaker[] = [];
const EMPTY_KEYWORDS: TranscriptKeyword[] = [];

const mergeWithHighlight =
  (highlights: SessionHighlight[] = []) =>
  (item: TranscriptSummaryItem) => {
    const matchingHighlight = find({ id: item.id }, highlights);

    // if there's no matching highlight, default to showing the summary item as it was
    // returned from the transcript endpoint
    if (!matchingHighlight) return item;

    const { description, type, ...highlight } = matchingHighlight;
    return {
      ...highlight,
      value: description,
      type: type as TranscriptClipType,
    };
  };

const populateSummaryTopics = (
  summary: TranscriptSummary,
  highlights: SessionHighlight[]
) => {
  const highlightsByType = groupBy("type", highlights) as Record<
    SessionHighlight["type"],
    SessionHighlight[] | undefined
  >;
  const mergedTopics = map(
    mergeWithHighlight(highlightsByType.topic),
    summary.topics
  );
  return sortBy("relativeStartTime", mergedTopics);
};

const populateSummaryItems = (
  summary: TranscriptSummary,
  highlights: SessionHighlight[],
  chapters: SessionChapter[]
): TranscriptSummary => {
  const highlightsByType = groupBy("type", highlights) as Record<
    SessionHighlight["type"],
    SessionHighlight[] | undefined
  >;

  // the transcript response does not include items that have been deleted, but
  // the highlight clips response does. so we know that these items have been
  // edited if 1) the transcript summary response includes an edited item or
  // 2) there is at least 1 deleted item of this type
  const actionItemsHaveBeenEdited =
    summary.editing.isActionItemsEdited ||
    anyAreDeleted(highlightsByType.action_item);
  const keyQuestionsHaveBeenEdited =
    summary.editing.isKeyQuestionsEdited ||
    anyAreDeleted(highlightsByType.key_question);
  const topicsHaveBeenEdited =
    summary.editing.isTopicsEdited || anyAreDeleted(highlightsByType.topic);

  const mergedActionItems = map(
    mergeWithHighlight(highlightsByType.action_item),
    summary.actionItems
  );
  const { items: actionItems, isEdited: isActionItemsEdited } =
    getItemsAndEditedStatus(mergedActionItems, actionItemsHaveBeenEdited);

  const mergedKeyQuestions = map(
    mergeWithHighlight(highlightsByType.key_question),
    summary.keyQuestions
  );
  const { items: keyQuestions, isEdited: isKeyQuestionsEdited } =
    getItemsAndEditedStatus(mergedKeyQuestions, keyQuestionsHaveBeenEdited);

  const mergedTopics = map(
    mergeWithHighlight(highlightsByType.topic),
    summary.topics
  );
  const { items: topics, isEdited: isTopicsEdited } = getItemsAndEditedStatus(
    mergedTopics,
    topicsHaveBeenEdited
  );

  const isChaptersEdited = anyAreEdited(chapters);

  return {
    editing: {
      isEdited:
        summary.editing.isEdited ||
        isActionItemsEdited ||
        isKeyQuestionsEdited ||
        isTopicsEdited ||
        isChaptersEdited,
      isSummaryEdited: summary.editing.isSummaryEdited,
      isActionItemsEdited,
      isKeyQuestionsEdited,
      isTopicsEdited,
      isChaptersEdited,
    },
    summary: summary.summary,
    actionItems,
    keyQuestions,
    topics,
    chapters,
  };
};

const parseChapters = (
  chapters: BaseSessionChapter[],
  meetingStartTime: DateTime,
  videoStartTimeOffset: number,
  topics: TranscriptSummary["topics"]
): SessionChapter[] => {
  if (isEmpty(chapters)) return EMPTY_CHAPTERS;

  const parsedChapters = chapters.map((chapter) => {
    const chapterRelativeStartTime = chapter.startTime
      .diff(meetingStartTime)
      .plus({ seconds: videoStartTimeOffset })
      .as("seconds");
    const chapterRelativeEndTime = chapter.endTime
      .diff(meetingStartTime)
      .plus({ seconds: videoStartTimeOffset })
      .as("seconds");

    const topicsForChapter = topics.filter((t) =>
      isWithinRange(t.relativeStartTime ?? 0, [
        chapterRelativeStartTime,
        chapterRelativeEndTime,
      ])
    );

    return {
      ...chapter,
      relativeStartTime: chapterRelativeStartTime,
      relativeEndTime: chapterRelativeEndTime,
      timestamp: getRelativeTimestamp(meetingStartTime, chapter.startTime),
      topics: topicsForChapter,
    };
  });

  const sortedChapters = sortBy("relativeStartTime", parsedChapters);

  sortedChapters.forEach((chapter: SessionChapter, index) => {
    const nextChapter = sortedChapters[index + 1];
    const previousChapter = sortedChapters[index - 1];
    chapter.nextChapterId = nextChapter?.id;
    chapter.previousChapterId = previousChapter?.id;
  });

  return sortedChapters;
};

/**
 * Creates Segments from SessionChapters. Assumes chapters are already sorted in
 * ascending order by start time, and will add a placeholder segment to capture
 * any time between the meeting start time and the start of the first chapter
 * (first chapter will usually align with the first transcript turn)
 * */
const createSegmentsFromChapters = (chapters: SessionChapter[]): Segment[] => {
  if (isEmpty(chapters)) return EMPTY_SEGMENTS;

  const segments = chapters.map((chapter) => ({
    id: chapter.id,
    label: chapter.title,
    startTime: chapter.relativeStartTime,
    endTime: chapter.relativeEndTime,
  }));

  const firstChapter = first(chapters)!;

  if (firstChapter.relativeStartTime > 0) {
    segments.unshift({
      id: "placeholder",
      label: "",
      startTime: 0,
      endTime: firstChapter.relativeStartTime,
    });
  }

  return segments;
};

const makeTurnsFromParagraphs = (
  turns: BaseTranscriptTurn[],
  meetingStart: DateTime
): TranscriptTurnParagraph[] =>
  flatMap((turn) => {
    const rawRelativeTimestamp = msDurationBetween(
      meetingStart,
      turn.startTime
    );

    return map(
      (paragraph) => ({
        ...paragraph,
        turn: {
          id: turn.id,
          isEdited: turn.isEdited,
          paragraphs: turn.paragraphs,
          previousTurnId: turn.previousTurnId,
          speaker: turn.speaker,
          startTime: turn.startTime,
          endTime: turn.endTime,
          rawStartTime: turn.rawStartTime,
          rawEndTime: turn.rawEndTime,
          relativeStartTime: {
            raw: rawRelativeTimestamp,
            formatted: getFormattedTimestampFromMs(rawRelativeTimestamp),
          },
        },
      }),
      turn.paragraphs
    );
  }, turns);

/** This hook handles fetching the transcript (turns, keywords, summary, etc.)
 * as well as chapters */
const useTranscript = (opts = DEFAULT_OPTS) => {
  const { sessionId } = useParams();
  const { canAccessTranscript: canAccessTranscriptPermission } =
    useFeatureAccess();

  const {
    data: { isRestrictedReport, shouldUpsellTranscriptEntities },
  } = useSessionMetadata({ enabled: opts.enabled });

  const queryOpts = { enabled: opts.enabled && !isRestrictedReport };

  const { data: transcript, isLoading: transcriptLoading } =
    useTranscriptQuery(queryOpts);

  const { meetingStartTime, isLoading: meetingReportIsLoading } =
    useMeetingReport(queryOpts);

  const { allHighlights: highlights, isLoading: highlightsLoading } =
    useMeetingReportHighlights(queryOpts);

  const { chapters, isLoading: chaptersLoading } = useSessionChaptersQuery(
    sessionId,
    queryOpts
  );

  const canAccessTranscript =
    canAccessTranscriptPermission && !isRestrictedReport;

  const {
    allSpeakers,
    setTranscriptAvailable,
    setTranscriptItems,
    setAllSpeakers,
  } = useTranscriptStore((state) => ({
    allSpeakers: state.allSpeakers,
    setTranscriptAvailable: state.setTranscriptAvailable,
    setTranscriptItems: state.setTranscriptItems,
    setAllSpeakers: state.setAllSpeakers,
  }));

  useEffect(() => {
    if (!transcript?.speakers) return;

    // TODO (ENG-11127): fix this useTranscript hook so it doesn't redo a bunch
    //  of work wherever it's used. Either make it a context or move logic into
    //  a useQuery hook
    if (allSpeakers.length === transcript.speakers.length) {
      let hasSpeakerNameUpdates = false;
      const allSpeakersSet = new Set(allSpeakers.map((s) => s.name));
      for (const speaker of transcript.speakers) {
        if (!allSpeakersSet.has(speaker.name)) {
          hasSpeakerNameUpdates = true;
          break;
        }
      }
      // Only set the speakers in the store if they are different
      if (!hasSpeakerNameUpdates) return;
    }

    // handle cases like the sample report where the transcript speaker list
    // is empty
    if (!isEmpty(allSpeakers) && isEmpty(transcript?.speakers)) return;

    setAllSpeakers(transcript.speakers);
  }, [allSpeakers, transcript?.speakers]);

  const videoStartTimeOffset = useVideoStore(
    (state) => state.videoStartTimeOffset
  );

  const rawSummary = shouldUpsellTranscriptEntities
    ? UPSELL_SUMMARY_CONTENT
    : isRestrictedReport
      ? EMPTY_TRANSCRIPT_SUMMARY
      : transcript?.summary ?? EMPTY_TRANSCRIPT_SUMMARY;

  // these are the actual turns (each turn contains all paragraphs); in most
  // cases we want to be using the parsed turns
  const rawTurns: BaseTranscriptTurn[] = shouldUpsellTranscriptEntities
    ? UPSELL_TRANSCRIPT_TURNS
    : (canAccessTranscript ? transcript?.turns : EMPTY_TURNS) ?? EMPTY_TURNS;

  const meetingStart = meetingStartTime ?? rawTurns[0]?.rawStartTime;

  // turns where 1 paragraph = 1 turn
  const turns: TranscriptTurnParagraph[] = useMemo(
    () => makeTurnsFromParagraphs(rawTurns, meetingStart),
    [rawTurns]
  );

  const transcriptIsEmpty =
    !transcriptLoading &&
    isEmpty(rawTurns) &&
    every((val) => isEmpty(compact(val)), Object.values(rawSummary));

  useEffect(() => {
    setTranscriptAvailable(!isEmpty(rawTurns));
  }, [rawTurns]);

  const transcriptLanguage =
    transcript?.dominantLanguage ?? DEFAULT_LANGUAGE_CODE;
  const wordDelimiter = getDelimiterForLanguage(transcriptLanguage);

  // populate reactions into the store
  useTranscriptReactions(turns, wordDelimiter);

  const isLoading =
    meetingReportIsLoading ||
    highlightsLoading ||
    transcriptLoading ||
    chaptersLoading;

  const parsedChapters: SessionChapter[] = useMemo(() => {
    if (shouldUpsellTranscriptEntities) return UPSELL_SUMMARY_CONTENT.chapters;
    if (!meetingStartTime || isRestrictedReport) return [];
    return parseChapters(
      chapters ?? [],
      meetingStartTime,
      videoStartTimeOffset,
      populateSummaryTopics(rawSummary, highlights)
    );
  }, [
    isLoading,
    rawSummary,
    videoStartTimeOffset,
    chapters,
    isRestrictedReport,
    shouldUpsellTranscriptEntities,
  ]);

  const summary = useMemo(() => {
    if (isRestrictedReport) return rawSummary;
    return populateSummaryItems(rawSummary, highlights, parsedChapters);
  }, [
    isLoading,
    videoStartTimeOffset,
    parsedChapters,
    rawSummary,
    isRestrictedReport,
  ]);

  const segments = useMemo(() => {
    if (isRestrictedReport) return [];
    return createSegmentsFromChapters(parsedChapters);
  }, [parsedChapters, isRestrictedReport]);

  const transcriptItems: TranscriptItem[] = useMemo(
    () => generateTranscriptItems(parsedChapters, turns),
    [parsedChapters, turns]
  );

  useEffect(() => {
    setTranscriptItems(transcriptItems);
  }, [transcriptItems]);

  return {
    isEmpty: transcriptIsEmpty,
    isLoading,
    keywords: transcript?.keywords ?? EMPTY_KEYWORDS,
    meetingStartTime,
    segments,
    summary,
    turns,
    transcriptItems,
    isEdited: Boolean(transcript?.isEdited),
    speakers: transcript?.speakers ?? EMPTY_SPEAKERS,
    rawTurns,
    transcriptLanguage,
    /** Determines which character words are split by in the given transcript's language */
    wordDelimiter,
  };
};

export default useTranscript;
